import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { useSendChatMessage } from '~/hooks/apis/ai-chat/useSendChatMessage';
import useCurrentUser from '~/hooks/useCurrentUser';

import { getOrder } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import { AiChatContext, ChatConversation, ChatMessage, GENERAL_CONVERSATION_ID } from './AiChatContext';
import { extractDepartmentSelection } from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS } from './chatModes';
import { loadConversations, saveConversations } from './chatStorage';
import { generateConversationRef } from './conversationRef';
import { useCurrentCustomerId } from './useCurrentCustomerId';

interface AiChatProviderProps {
  children: React.ReactNode;
}

function createConversation(
  id: string,
  customerId: string | null,
  customerName: string | null,
  orderId: string | null,
): ChatConversation {
  const now = Date.now();
  return {
    id,
    conversationRef: generateConversationRef(),
    customerId,
    customerName,
    orderId,
    department: null,
    messages: [],
    lastActivityAt: now,
    createdAt: now,
  };
}

export default function AiChatProvider({ children }: AiChatProviderProps) {
  const [conversations, setConversations] = useState<Record<string, ChatConversation>>(() => loadConversations());
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useSendChatMessage();
  const { user } = useCurrentUser();

  const { customerId: urlCustomerId, orderId: urlOrderId } = useCurrentCustomerId();

  const orderCacheRef = useRef<Record<string, { customerId: string; customerName: string }>>({});
  const [resolvedCustomer, setResolvedCustomer] = useState<{
    customerId: string;
    customerName: string;
  } | null>(null);

  useEffect(() => {
    if (!urlOrderId) {
      setResolvedCustomer(null);
      return;
    }

    const cached = orderCacheRef.current[urlOrderId];
    if (cached) {
      setResolvedCustomer(cached);
      return;
    }

    let cancelled = false;
    getOrder(urlOrderId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const order = response?.result;
        if (order?.fk_customer_id) {
          const result = {
            customerId: order.fk_customer_id,
            customerName: order.customer_full_name || order.customer_email || 'Unknown',
          };
          orderCacheRef.current[urlOrderId] = result;
          setResolvedCustomer(result);
        }
      })
      .catch(() => {
        // Order fetch failed -- fall back to general chat
      });

    return () => {
      cancelled = true;
    };
  }, [urlOrderId]);

  const currentUrlCustomerId = urlCustomerId || resolvedCustomer?.customerId || null;
  const currentUrlCustomerName = resolvedCustomer?.customerName || null;
  const currentUrlOrderId = urlOrderId || null;

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const debouncedSave = useMemo(() => debounce(saveConversations, 500), []);
  useEffect(() => {
    debouncedSave(conversations);
  }, [conversations, debouncedSave]);
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  const getOrCreateConversation = useCallback(
    (
      convId: string,
      customerId: string | null,
      customerName: string | null,
      orderId: string | null,
    ): ChatConversation => {
      if (conversations[convId]) {
        return conversations[convId];
      }
      const conv = createConversation(convId, customerId, customerName, orderId);
      setConversations((prev) => ({ ...prev, [convId]: conv }));
      return conv;
    },
    [conversations],
  );

  const resolveCustomerName = useCallback(
    async (customerId: string): Promise<string> => {
      const existing = conversations[customerId];
      if (existing?.customerName) {
        return existing.customerName;
      }

      if (resolvedCustomer?.customerId === customerId && resolvedCustomer.customerName) {
        return resolvedCustomer.customerName;
      }

      try {
        const user = await UsersService.getUser(customerId);
        return user?.fullName || user?.email || 'Unknown';
      } catch {
        return 'Unknown';
      }
    },
    [conversations, resolvedCustomer],
  );

  const openChat = useCallback(
    async (options?: { customerId?: string; customerName?: string }) => {
      const targetCustomerId = options?.customerId || currentUrlCustomerId;
      const convId = targetCustomerId || GENERAL_CONVERSATION_ID;

      let customerName = options?.customerName || null;

      if (targetCustomerId && !customerName) {
        customerName = conversations[convId]?.customerName || currentUrlCustomerName || null;
        if (!customerName) {
          customerName = await resolveCustomerName(targetCustomerId);
        }
      }

      getOrCreateConversation(convId, targetCustomerId, customerName, currentUrlOrderId);

      if (targetCustomerId && customerName && conversations[convId] && !conversations[convId].customerName) {
        setConversations((prev) => ({
          ...prev,
          [convId]: { ...prev[convId], customerName },
        }));
      }

      // Keep orderId up to date for the active conversation
      if (currentUrlOrderId && conversations[convId] && conversations[convId].orderId !== currentUrlOrderId) {
        setConversations((prev) => ({
          ...prev,
          [convId]: { ...prev[convId], orderId: currentUrlOrderId },
        }));
      }

      setActiveConversationId(convId);
      setError(null);
      setIsOpen(true);
    },
    [
      currentUrlCustomerId,
      currentUrlCustomerName,
      currentUrlOrderId,
      conversations,
      getOrCreateConversation,
      resolveCustomerName,
    ],
  );

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const switchConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
    setError(null);
  }, []);

  const deleteConversation = useCallback(
    (conversationId: string) => {
      setConversations((prev) => {
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });

      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
      }
    },
    [activeConversationId],
  );

  const clearChat = useCallback(() => {
    if (!activeConversationId) {
      return;
    }
    setConversations((prev) => ({
      ...prev,
      [activeConversationId]: {
        ...prev[activeConversationId],
        conversationRef: generateConversationRef(),
        messages: [],
        lastActivityAt: Date.now(),
        createdAt: Date.now(),
      },
    }));
    setError(null);
  }, [activeConversationId]);

  const activeConversation = activeConversationId ? conversations[activeConversationId] || null : null;

  const sendMessage = useCallback(
    (content: string) => {
      if (!activeConversationId) {
        return;
      }
      const conv = conversationsRef.current[activeConversationId];
      if (!conv) {
        return;
      }

      const selectedDepartment = extractDepartmentSelection(content, DEPARTMENT_OPTIONS);
      const userMessage: ChatMessage = { role: 'user', content };
      const updatedMessages = [...conv.messages, userMessage];
      const targetConvId = activeConversationId;

      setConversations((prev) => ({
        ...prev,
        [targetConvId]: {
          ...prev[targetConvId],
          department: selectedDepartment || prev[targetConvId].department || null,
          messages: updatedMessages,
          lastActivityAt: Date.now(),
        },
      }));
      setError(null);

      mutation.mutate(
        {
          context: {
            conversationRef: conv.conversationRef,
            agentId: user?.id_member || null,
            customerId: conv.customerId,
            customerName: conv.customerName,
            orderId: conv.orderId || currentUrlOrderId,
            department: selectedDepartment || conv.department || null,
          },
          messages: updatedMessages,
        },
        {
          onSuccess: (response) => {
            setConversations((prev) => {
              const current = prev[targetConvId];
              if (!current) {
                return prev;
              }
              return {
                ...prev,
                [targetConvId]: {
                  ...current,
                  messages: [
                    ...current.messages,
                    {
                      role: 'assistant',
                      content: response.content,
                      toolCalls: response.toolCalls.length > 0 ? response.toolCalls : undefined,
                      responseId: response.responseId || undefined,
                    },
                  ],
                  lastActivityAt: Date.now(),
                },
              };
            });
          },
          onError: (err) => {
            setError(err.message || 'Failed to get a response. Please try again.');
          },
        },
      );
    },
    [activeConversationId, currentUrlOrderId, mutation, user?.id_member],
  );

  const value = useMemo(
    () => ({
      isOpen,
      conversations,
      activeConversationId,
      activeConversation,
      isLoading: mutation.isPending,
      error,
      currentUrlCustomerId,
      currentUrlOrderId,
      openChat,
      closeChat,
      sendMessage,
      clearChat,
      switchConversation,
      deleteConversation,
    }),
    [
      isOpen,
      conversations,
      activeConversationId,
      activeConversation,
      mutation.isPending,
      error,
      currentUrlCustomerId,
      currentUrlOrderId,
      openChat,
      closeChat,
      sendMessage,
      clearChat,
      switchConversation,
      deleteConversation,
    ],
  );

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
}
